import { MANTRA_ROLES, canonicalMantraRoles } from './studioCalc.js';

const REF_ROSTER = 29;
const REF_GK = 3;

/**
 * Target ruolo per modulo @ Max 29 — calibrati con simulatore
 * docs/superpowers/specs/rosa-module-targets-simulator.html (export utente 2026-08-29).
 */
export const ROSA_MODULE_TARGETS_REF_29 = {
  '3-4-3': {
    P: 3, DS: 0, DC: 5, DD: 0, B: 2, E: 5, M: 2, C: 4, W: 0, T: 0, A: 5, PC: 3,
  },
  '3-4-1-2': {
    P: 3, DS: 0, DC: 5, DD: 0, B: 2, E: 5, M: 2, C: 4, W: 0, T: 3, A: 0, PC: 5,
  },
  '3-4-2-1': {
    P: 3, DS: 0, DC: 5, DD: 0, B: 2, E: 3, M: 1, C: 4, W: 2, T: 3, A: 3, PC: 3,
  },
  '3-5-2': {
    P: 3, DS: 0, DC: 5, DD: 0, B: 2, E: 4, M: 3, C: 4, W: 3, T: 0, A: 0, PC: 5,
  },
  '3-5-1-1': {
    P: 3, DS: 0, DC: 4, DD: 0, B: 1, E: 2, M: 4, C: 3, W: 4, T: 2, A: 3, PC: 3,
  },
  '4-3-3': {
    P: 3, DS: 2, DC: 5, DD: 2, B: 0, E: 3, M: 1, C: 5, W: 3, T: 0, A: 0, PC: 5,
  },
  '4-3-1-2': {
    P: 3, DS: 2, DC: 6, DD: 2, B: 0, E: 0, M: 3, C: 5, W: 0, T: 3, A: 0, PC: 5,
  },
  '4-4-2': {
    P: 3, DS: 2, DC: 5, DD: 2, B: 0, E: 3, M: 1, C: 5, W: 3, T: 0, A: 0, PC: 5,
  },
  '4-1-4-1': {
    P: 3, DS: 2, DC: 5, DD: 2, B: 0, E: 1, M: 3, C: 0, W: 5, T: 5, A: 0, PC: 3,
  },
  '4-4-1-1': {
    P: 3, DS: 2, DC: 5, DD: 2, B: 0, E: 0, M: 3, C: 3, W: 5, T: 2, A: 1, PC: 3,
  },
  '4-2-3-1': {
    P: 3, DS: 2, DC: 4, DD: 2, B: 0, E: 0, M: 3, C: 3, W: 3, T: 3, A: 3, PC: 3,
  },
};

function emptyTargets() {
  return Object.fromEntries(MANTRA_ROLES.map((r) => [r, 0]));
}

/** Largest remainder: scale weights to `pool` integers. */
export function allocateByLargestRemainder(weightsByRole, pool) {
  const out = emptyTargets();
  if (pool <= 0) return out;
  const entries = MANTRA_ROLES
    .map((role) => ({ role, w: Number(weightsByRole[role]) || 0 }))
    .filter((e) => e.w > 0);
  const wSum = entries.reduce((s, e) => s + e.w, 0);
  if (wSum <= 0) return out;
  const exact = entries.map((e) => ({ role: e.role, raw: (e.w / wSum) * pool }));
  let used = 0;
  for (const e of exact) {
    out[e.role] = Math.floor(e.raw);
    used += out[e.role];
  }
  let remain = pool - used;
  exact
    .map((e) => ({ role: e.role, frac: e.raw - Math.floor(e.raw) }))
    .sort((a, b) => b.frac - a.frac || MANTRA_ROLES.indexOf(a.role) - MANTRA_ROLES.indexOf(b.role))
    .forEach((e) => {
      if (remain <= 0) return;
      out[e.role] += 1;
      remain -= 1;
    });
  return out;
}

function scaleModuleTargets(ref, maxPlayers) {
  if (maxPlayers === REF_ROSTER) {
    return Object.fromEntries(MANTRA_ROLES.map((r) => [r, ref[r] || 0]));
  }
  return allocateByLargestRemainder(ref, maxPlayers);
}

/**
 * Target ruolo per modulo: tabella calibrata @ Max 29, scalata proporzionalmente ad altri Max.
 */
export function buildModuleRoleTargets(moduleId, maxPlayers) {
  const max = Math.max(0, Number(maxPlayers) || 0);
  if (max === 0) return emptyTargets();

  const ref = ROSA_MODULE_TARGETS_REF_29[moduleId];
  if (!ref) {
    const targets = emptyTargets();
    let targetP = Math.round((REF_GK * max) / REF_ROSTER);
    targetP = Math.min(max, Math.max(0, targetP));
    if (max >= 1 && targetP === 0) targetP = 1;
    targets.P = targetP;
    return targets;
  }

  return scaleModuleTargets(ref, max);
}

export function countOwnedByRole(players) {
  const owned = emptyTargets();
  for (const p of players || []) {
    for (const r of canonicalMantraRoles(p?.roles ?? [])) {
      owned[r] += 1;
    }
  }
  return owned;
}

export function buildRoleCoverageRows(owned, targets) {
  const rows = [];
  for (const role of MANTRA_ROLES) {
    const target = Number(targets?.[role]) || 0;
    if (target <= 0) continue;
    const o = Number(owned?.[role]) || 0;
    let status = 'empty';
    if (o >= target) status = 'ok';
    else if (o > 0) status = 'partial';
    rows.push({ role, owned: o, target, status });
  }
  return rows;
}
